import { Check, ElectricBolt, Error, QuestionMark, Settings, WbSunnyOutlined } from '@mui/icons-material'
import { ConsumptionStatWidgetConfig, DashboardViewProps, TestViewProps, Widget } from './Widget'
import { Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { sprintf } from 'sprintf-js'
import { lang } from '../../utils/lang'
import { isDebug } from '../../utils/misc'
import { P3PowerMeterPortControl } from '../ports/P3PowerMeterPortControl'
import { Trans } from 'react-i18next'
import { StatusLight } from '../../components/atoms/StatusLight'
import { Button } from '../../components/atoms/Button'
import { Stars } from '../../components/atoms/Stars'
import { SignalLevel } from '../../components/atoms/SignalLevel'
import { PortValuesEm3Ph } from '../../types/ports'
import { SimplePowerMeterPortControl } from '../ports/SimplePowerMeterPortControl'
import { HanPowerMeterPortControl } from '../ports/HanPowerMeterPortControl'
import Carousel from 'react-multi-carousel'
import 'react-multi-carousel/lib/styles.css'
import React from 'react'
import { NetsumPowerMeterPortControl } from '../ports/NetsumPowerMeterPortControl'

// TODO: Typo in filename.

interface CustomWidgetProps {
  children?: React.ReactNode;
  next?: () => void,
  hasData: boolean
}

const carouselResponsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  }
}

export class ConsumptionStatWidget extends Widget<{}, ConsumptionStatWidgetConfig> {
  get Icon() {
    return ElectricBolt
  }

  get width(): number {
    return 2
  }

  get height(): number {
    return 4
  }

  get borderRadius(): string {
    return '1.875rem'
  }

  get textColor(): string {
    return 'primary.contrastText'
  }

  get bgColor(): string {
    return 'primary.main'
  }

  get meterControl(): null | P3PowerMeterPortControl | SimplePowerMeterPortControl | HanPowerMeterPortControl | NetsumPowerMeterPortControl {
    const meters = this.controls.filter(ctrl => ['EM_3PH', 'EM_HAN', 'EM_SIMPLE', 'EM_NETSUM'].includes(ctrl.port.type))
    if (meters.length) {
      return meters[0] as P3PowerMeterPortControl | SimplePowerMeterPortControl | HanPowerMeterPortControl | NetsumPowerMeterPortControl
    }

    return null
  }

  getName(): string {
    if (this.name) return this.name
    const meters = this.appliances.filter(a => a.type === 'ENERGY_METER')
    if (meters.length) {
      return meters[0].getName()
    }
    return this.appliances.length ? this.appliances[0].getName() : ''
  }

  draw(props: DashboardViewProps): React.ReactElement {
    let consumption: number | null = null
    let production: number | null = null
    let energyTotal: number | null = null

    const ctrl: P3PowerMeterPortControl | HanPowerMeterPortControl | NetsumPowerMeterPortControl = this.meterControl as P3PowerMeterPortControl | HanPowerMeterPortControl | NetsumPowerMeterPortControl
    const { cur_current } = ctrl.port.values

    const ctrl2: P3PowerMeterPortControl | {} = this.meterControl?.port.type === 'EM_3PH' ? this.meterControl as P3PowerMeterPortControl : {}
    const phase_sensor_signal_level = (ctrl2 as P3PowerMeterPortControl)?.port?.values?.phase_sensor_signal_level
    const enablePhaseSensing = (ctrl2 && 'getConfig' in ctrl2) ? ctrl2.getConfig('enable_phase_sensing') as boolean : false

    let phasesList: { 'pf': number, 'real_power': number}[] | null = []

    this.controls.forEach(ctrl => {
      const power = ctrl.getValue<number|null>('power')
      if (power === undefined) return
      const energy = ctrl.getValue<number|null>('energy')
      const phases = ctrl.getValue<{ 'pf': number, 'real_power': number}[] | null>('phases')
      if (power !== null) {
        if (ctrl.port.type === 'SOLAR') {
          production = (production || 0) + power / 1000
        } else {
          consumption = (consumption || 0) + power / 1000
          energyTotal = (energyTotal || 0) + (energy || 0)
          if (phases) {
            phasesList = phases
          }
        }
      }
    })

    const total = consumption === null && production === null ? null : (consumption || 0) + (production || 0)
    const value = lang.num(total, isDebug('high-accuracy') ? 3 : 1, 'kW')
    const energyValue = lang.num((energyTotal || 0) / 3.6e6, isDebug('high-accuracy') ? 3 : 1, 'kWh')

    const CurrentSection = () => {
      return <Box>
          <>
            <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor, userSelect: 'none' }}>
              {cur_current === undefined || cur_current[0] === undefined ? <>— A</> : `${cur_current[0]} A`}
            </Box>
            <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor, userSelect: 'none' }}>
              {cur_current === undefined || cur_current[1] === undefined ? <>— A</> : `${cur_current[1]} A`}
            </Box>
            <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor, userSelect: 'none' }}>
              {cur_current === undefined || cur_current[2] === undefined ? <>— A</> : `${cur_current[2]} A`}
            </Box>
          </>
          </Box>
    }

    const PerPhaseRealPowerSection = phasesList.length > 0 && <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor }}>
      <Box>
        <Stack direction="row" spacing={2} sx={{ userSelect: 'none' }}>
          <Box>
            {phasesList === undefined || phasesList[0] === undefined ? <>— W</> : `${phasesList[0].real_power} W`}
          </Box>
          <Box>
            <Trans>PF</Trans>: {phasesList === undefined || phasesList[0] === undefined || phasesList[0].pf === null ? <>—</> : phasesList[0].pf.toString()}
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ userSelect: 'none' }}>
          <Box>
            {phasesList === undefined || phasesList[1] === undefined ? <>— W</> : `${phasesList[1].real_power} W`}
          </Box>
          <Box>
            <Trans>PF</Trans>: {phasesList === undefined || phasesList[1] === undefined || phasesList[1].pf === null ? <>—</> : phasesList[1].pf.toString()}
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ userSelect: 'none' }}>
          <Box>
            {phasesList === undefined || phasesList[2] === undefined ? <>— W</> : `${phasesList[2].real_power} W`}
          </Box>
          <Box>
            <Trans>PF</Trans>: {phasesList === undefined || phasesList[2] === undefined || phasesList[2].pf === null ? <>—</> : phasesList[2].pf.toString()}
          </Box>
        </Stack>
      </Box>
    </Box>

    const CalibrationSection = () => {
      const ctrl: P3PowerMeterPortControl = this.meterControl as P3PowerMeterPortControl
      const { calib_status, phase_sensor_signal_level } = ctrl.port.values

      return (
        <Box>
          <Box>
            {phase_sensor_signal_level !== 0
              ? <Box sx={{ userSelect: 'none' }}>
              <Box sx={{ fontSize: '1rem', fontWeight: 700, color: this.textColor }}><Trans>Calibration</Trans></Box>
              <Box>
                <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor }}>
                  <Trans>L1</Trans>: <Stars widget count={calib_status ? calib_status[0] : 0} max={3}/>
                  &nbsp;
                </Box>
                <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor }}>
                  <Trans>L2</Trans>: <Stars widget count={calib_status ? calib_status[1] : 0} max={3}/>
                  &nbsp;
                </Box>
                <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor }}>
                  <Trans>L3</Trans>: <Stars widget count={calib_status ? calib_status[2] : 0} max={3}/>
                  &nbsp;
                </Box>
              </Box>
            </Box>
              : <Box sx={{ fontSize: '1rem', fontWeight: 400, color: this.textColor }}><Trans>Phase sensor failure</Trans></Box>}
          </Box>
        </Box>
      )
    }

    function CustomWidget(props: CustomWidgetProps) {
      const { next, children, hasData } = props

      const handleGoToNextWidget = () => {
        if (hasData && next) {
          return next()
        }
      }

      return (
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }} onClick={handleGoToNextWidget} >
        {children}
      </Box>
      )
    }

    const powerWidget = <Box>
    <Typography className="big-initial" sx={{
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: '2.6rem',
      color: this.textColor,
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.6rem',
      color: this.textColor
    }}>
      {props.testing ? energyValue : this.getName()}
    </Typography>
    <Box sx={{
      mt: '0.9rem',
      display: 'grid',
      gridTemplateColumns: 'auto 1.5rem',
    }}>
      <Box sx={{
        display: 'grid',
        height: '1.8rem',
        gridTemplateColumns: 'auto auto',
        fontSize: '1rem',
      }}>
        { production !== null
          ? <Box sx={{ display: 'grid', gridTemplateColumns: '2rem auto', color: 'primary.contrastText' }}>
              <WbSunnyOutlined sx={{ mr: 4 }}/>
              <Box>{sprintf(`%.${isDebug('high-accuracy') ? 4 : 2}f kW`, production)}</Box>
            </Box>
          : <></>
        }
      </Box>
    </Box>
  </Box>

    return (
      <Box data-cy={`box-${this.getName()}`} sx={{ height: '100%', padding: '1rem 1rem 0.5rem 1rem', position: 'relative', overflow: 'hidden' }}>
        <Carousel containerClass='carousel' customButtonGroup={<CustomWidget hasData={!!value} />} arrows={false} infinite autoPlay={false} autoPlaySpeed={3000} dotListClass='dot-list' responsive={carouselResponsive} draggable showDots={!!value}>
          {/* Page 1 */}
          <Box id={`widget-power-${this.getName()}`} data-cy={`box-${this.getName()}`} sx={{ height: '100%' }}>
            {this.meterControl?.port.type !== 'EM_3PH' && powerWidget}
            {this.meterControl?.port.type === 'EM_3PH' ? (enablePhaseSensing ? (phase_sensor_signal_level === 0 ? <Box>{this.app.t('State-ERROR')}</Box> : powerWidget) : powerWidget) : <></>}
          </Box>
          {/* Page 2 */}
          <Box id={`widget-currents-${this.getName()}`} >
            {CurrentSection()}
          </Box>
          {/* Page 3 */}
          <Box id={`widget-perphase-${this.getName()}`} >{PerPhaseRealPowerSection}</Box>
          {/* Page 4 */}
          {this.meterControl?.port.type === 'EM_3PH' && enablePhaseSensing && <Box id={`widget-calibration-${this.getName()}`} >{CalibrationSection()}</Box>}
        </Carousel>
      </Box>
    )
  }

  drawTestView(props: TestViewProps): React.ReactElement {
    if (this.meterControl?.port.type === 'EM_3PH') {
      return this.drawCalibrationView()
    }
    return this.dashboardView({
      testing: true,
      refresh: () => null
    })
  }

  drawCalibrationView(): React.ReactElement {
    const ctrl: P3PowerMeterPortControl = this.meterControl as P3PowerMeterPortControl
    if (ctrl.getConfig('enable_phase_sensing')) {
      return this.drawBidirectionalCalibrationView()
    }
    return this.drawUnidirectionalCalibrationView()
  }

  drawMeasuringData(angle: boolean, signalLevels: boolean): React.ReactElement {
    const ctrl: P3PowerMeterPortControl = this.meterControl as P3PowerMeterPortControl
    const { energy, power, cur_current, current_level, disconnected, phase_sensor_signal_level } = ctrl.port.values
    return <Box>
      <Trans>Measuring Data</Trans>
      <Box sx={{ mt: 2 }}>
        <Trans>Power</Trans>: {power === undefined ? <>—</> : `${sprintf('%f', power)} W`}
      </Box>
      <Box>
        <Trans>Energy</Trans>: {energy === undefined ? <>—</> : `${sprintf('%.3f', energy / 3.6e6)} kWh`}
      </Box>
      <Box sx={{ mt: 1 }}>
        <Trans>Current 1</Trans>:
        <> </>
        <StatusLight status={disconnected && disconnected[0] ? 'none' : 'success'} sx={{ display: 'inline-block', mx: 1 }}/>
        {cur_current === undefined || cur_current[0] === undefined ? <>—</> : `${cur_current[0]} A`}
        {signalLevels && <SignalLevel min={0} max={10} level={(current_level && current_level[0]) || 0} bad={2} good={5}/>}
      </Box>
      <Box>
        <Trans>Current 2</Trans>:
        <> </>
        <StatusLight status={disconnected && disconnected[1] ? 'none' : 'success'} sx={{ display: 'inline-block', mx: 1 }}/>
        {cur_current === undefined || cur_current[1] === undefined ? <>—</> : `${cur_current[1]} A`}
        {signalLevels && <SignalLevel min={0} max={10} level={(current_level && current_level[1]) || 0} bad={2} good={5}/>}
      </Box>
      <Box>
        <Trans>Current 3</Trans>:
        <> </>
        <StatusLight status={disconnected && disconnected[2] ? 'none' : 'success'} sx={{ display: 'inline-block', mx: 1 }}/>
        {cur_current === undefined || cur_current[2] === undefined ? <>—</> : `${cur_current[2]} A`}
        {signalLevels && <SignalLevel min={0} max={10} level={(current_level && current_level[2]) || 0} bad={2} good={5}/>}
      </Box>
      {
        angle &&
        <Box>
          <Trans>Angle</Trans>:
          <> </>
          <StatusLight status={disconnected && disconnected[3] ? 'none' : 'success'} sx={{ display: 'inline-block', mx: 1 }}/>
          {phase_sensor_signal_level === undefined ? <>—</> : `${phase_sensor_signal_level} / 10`}
          {signalLevels && <SignalLevel min={0} max={10} level={phase_sensor_signal_level || 0} bad={2} good={5}/>}
        </Box>
      }
    </Box>
  }

  drawUnidirectionalCalibrationView(): React.ReactElement {
    const ctrl: P3PowerMeterPortControl = this.meterControl as P3PowerMeterPortControl
    const [, text] = this.getHelpText(ctrl.port.values, true)

    return <Box>
      {this.drawMeasuringData(false, false)}
      <Box sx={{ mt: 3 }}>
        {text}
      </Box>
    </Box>
  }

  getHelpText(values: PortValuesEm3Ph, unidirectional: boolean = false): [React.ReactElement, string] {
    const disconnected = values.disconnected || [false, false, false, false]
    const calib_status = values.calib_status || [0, 0, 0]
    const ok = <Check sx={{ color: 'success.main', width: '4rem', height: '4rem' }} />
    const fail = <Error sx={{ color: 'error.light', width: '4rem', height: '4rem' }} />
    const q = <QuestionMark sx={{ color: 'secondary.main', width: '4rem', height: '4rem' }} />
    const working = <Settings className="rotating-slow" sx={{ color: 'secondary.main', width: '4rem', height: '4rem' }} />

    if (disconnected[0]) {
      return [fail, this.app.t('Current transformer (L1) disconnected. Check connections.')]
    }
    if (disconnected[1]) {
      return [fail, this.app.t('Current transformer (L2) disconnected. Check connections.')]
    }
    if (disconnected[2]) {
      return [fail, this.app.t('Current transformer (L3) disconnected. Check connections.')]
    }

    if (unidirectional) {
      return [ok, this.app.t('Energy meter has been configured unidirectional. No need for calibration.')]
    }

    if (disconnected[3]) {
      return [fail, this.app.t('Phase angle sensor nonfunctional. Connect to port 20. Attach the sensing tip firmly over the insulated phase conductor. Secure the cable properly for its full length, avoiding other phase conductors or sources of interference.')]
    }

    if (values.calibrating) {
      const text = this.app.t('Calibrating. Keep solar inverter disconnected.') + ' '

      const fails: string[] = []
      if (calib_status[0] === 0) {
        fails.push('[L1]')
      }
      if (calib_status[1] === 0) {
        fails.push('[L2]')
      }
      if (calib_status[2] === 0) {
        fails.push('[L3]')
      }

      if (fails.length === 0) {
        return [ok, text + this.app.t('Calibration acceptable. You can try to improve the result if you wish. Before connecting solar inverter, press STOP CALIBRATION!')]
      }
      if (fails.length === 1) {
        return [working, text + this.app.t('Try to add load to phase {phase}. If unsuccessful, calibration remains incomplete and is automatically improved later. Before connecting solar inverter, press STOP CALIBRATION!').replace('{phase}', fails[0])]
      }
      if (fails.length > 1) {
        return [working, text + this.app.t('Try to add load to phases {phases}. If unsuccessful, calibration remains incomplete and is automatically improved later. Before connecting solar inverter, press STOP CALIBRATION!').replace('{phases}', fails.join(', '))]
      }
    } else {
      if (calib_status[0] === 0 && calib_status[1] === 0 && calib_status[2] === 0) {
        return [q, this.app.t('Not calibrated. Disconnect the solar inverter, turn on resistive loads on phases and press CALIBRATE.')]
      }
      if (calib_status[0] === 0 || calib_status[1] === 0 || calib_status[2] === 0) {
        return [q, this.app.t('Partially calibrated. If possible, recalibrate immediately: disconnect the solar inverter, turn on resistive loads on phases and press CALIBRATE. If impossible, calibration is automatically improved later.')]
      }
    }

    return [ok, this.app.t('Calibrated. Bidirectional metering on all phases.')]
  }

  drawBidirectionalCalibrationView(): React.ReactElement {
    const ctrl: P3PowerMeterPortControl = this.meterControl as P3PowerMeterPortControl
    const { calib_status, calibrating } = ctrl.port.values

    const onCalibrate = () => {
      ctrl.sendCommand({ command: 'CALIBRATE' })
    }
    const onCalibrateWithProduction = () => {
      ctrl.sendCommand({ command: 'CALIBRATE_WITH_PRODUCTION' })
    }
    const onStop = () => {
      ctrl.sendCommand({ command: 'STOP_CALIBRATION' })
    }
    const onRemove = () => {
      ctrl.sendCommand({ command: 'REMOVE_CALIBRATION' })
    }

    const [Icon, text] = this.getHelpText(ctrl.port.values)

    return (
      <Box sx={{ mt: 6 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          <Box sx={{ pr: '1rem', pb: '1rem' }}>
            {this.drawMeasuringData(true, true)}
          </Box>

          <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem' }}>
            <Trans>Calibration</Trans>
            {
              calib_status &&
              <Box sx={{ mt: 2 }}>
                <Box>
                  <Trans>L1</Trans>: <Stars count={calib_status[0]} max={3}/>
                  &nbsp;
                  {calib_status[0] > 0 ? this.app.t('calibrated') : this.app.t('waiting')}
                </Box>
                <Box>
                  <Trans>L2</Trans>: <Stars count={calib_status[1]} max={3}/>
                  &nbsp;
                  {calib_status[1] > 0 ? this.app.t('calibrated') : this.app.t('waiting')}
                </Box>
                <Box>
                  <Trans>L3</Trans>: <Stars count={calib_status[2]} max={3}/>
                  &nbsp;
                  {calib_status[2] > 0 ? this.app.t('calibrated') : this.app.t('waiting')}
                </Box>
              </Box>

            }
          </Box>
        </Box>

        <Box sx={{ my: '1rem', borderBottom: '1px solid #737373' }}/>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
          }}
        >
          <Box sx={{ pr: '1rem' }}>
            <Box sx={{ textAlign: 'center' }}>{Icon}</Box>
            <Box>{text}</Box>
          </Box>

          <Box sx={{ borderLeft: '1px solid #737373', pl: '1rem', mb: 4 }}>
            <Button sx={{ fontSize: '0.8rem', width: '100%', height: '1rem' }} disabled={calibrating} onClick={onCalibrate}><Trans>Calibrate</Trans></Button>
            <Button sx={{ fontSize: '0.7rem', width: '100%', height: '1rem', lineHeight: '0.75rem', mt: 2 }} disabled={calibrating} onClick={onCalibrateWithProduction}><Trans>Calibrate With Production</Trans></Button>
            <Button sx={{ fontSize: '0.7rem', width: '100%', height: '1rem', lineHeight: '0.75rem', mt: 2 }} disabled={!calibrating} onClick={onStop}><Trans>Stop Calibration</Trans></Button>
            <Button sx={{ fontSize: '0.7rem', width: '100%', height: '1rem', lineHeight: '0.75rem', mt: 2, backgroundColor: 'error.dark' }} disabled={calibrating} onClick={onRemove}><Trans>Remove Calibration</Trans></Button>
          </Box>
        </Box>
      </Box>
    )
  }
}
